import moment, { Moment } from "moment-timezone";
import { CalendarRow } from "../model/calendarRow";
import { CalendarColumn } from "../model/calendarColumn";
import { CalendarResource } from "../model/calendarResource";
import { TimeCell } from "../model/timeCell";
import { PropertyResource } from "../model/propertyResource";
import d from "debug";
import { GUIDED_TOUR } from "../components/constants";

const debug = d("CalendarUtils");

export const COLUMN_ID_FORMAT = "ddd-D";
export const ROW_ID_FORMAT = "h:mm a";
export const CELL_ID_FORMAT = "ddd-D-k";
export const HIDDEN_TYPE = "hidden";
export const RESOURCE_TYPE = "resource";
export const LEDGEND_TYPE = "ledgend";
export const TIME_COLUMN_ID = "time";
export const NONE_ID = "none";

export function getMinStartDate(propertyResources: PropertyResource[] | undefined): Moment | undefined {
  let minStartDate: Moment | undefined = undefined;
  if (propertyResources) {
    propertyResources.forEach((pr) => {
      if (pr.timeSlots) {
        pr.timeSlots.forEach((timeslot) => {
          if (!minStartDate) {
            minStartDate = moment(timeslot.startTime);
          } else if (minStartDate.isAfter(timeslot.startTime)) {
            minStartDate = moment(timeslot.startTime);
          }
        });
      }
    });
  }
  return minStartDate;
}

export function getMaxEndDate(propertyResources: PropertyResource[] | undefined): Moment | undefined {
  let maxEndDate: Moment | undefined = undefined;
  if (propertyResources) {
    propertyResources.forEach((pr) => {
      if (pr.timeSlots) {
        pr.timeSlots.forEach((timeslot) => {
          if (!maxEndDate) {
            maxEndDate = moment(timeslot.endTime);
          } else if (maxEndDate.isBefore(timeslot.endTime)) {
            maxEndDate = moment(timeslot.endTime);
          }
        });
      }
    });
  }
  return maxEndDate;
}

export function getMinStartHourOfDay(propertyResources: PropertyResource[], defaultValue: number = 9): number {
  let minStartHour: number | undefined = undefined;
  if (propertyResources) {
    propertyResources.forEach((pr) => {
      if (pr.timeSlots) {
        pr.timeSlots.forEach((timeslot) => {
          const timeslotStartHour = moment(timeslot.startTime).get("hour");
          if (!minStartHour) {
            minStartHour = timeslotStartHour;
          } else if (timeslotStartHour < minStartHour) {
            minStartHour = timeslotStartHour;
          }
        });
      }
    });
  }
  return minStartHour || defaultValue;
}

export function minStartHourOfDay(propertyResource: PropertyResource, defaultValue: number = 9): number {
  const array = [];
  array.push(propertyResource);
  return getMinStartHourOfDay(array) || defaultValue;
}

export function getMaxEndHourOfDay(propertyResources: PropertyResource[], defaultValue: number = 20): number {
  let maxEndHour: number | undefined = undefined;

  if (propertyResources) {
    propertyResources.forEach((pr) => {
      if (pr.timeSlots) {
        pr.timeSlots.forEach((timeslot) => {
          const timeslotEndHour = moment(timeslot.endTime).get("hour");
          if (!maxEndHour) {
            maxEndHour = timeslotEndHour;
          } else if (timeslotEndHour > maxEndHour) {
            maxEndHour = timeslotEndHour;
          }
        });
      }
    });
  }
  return maxEndHour || defaultValue;
}

export function maxEndHourOfDay(propertyResource: PropertyResource, defaultValue: number = 20): number {
  const array = [];
  array.push(propertyResource);
  return getMaxEndHourOfDay(array) || defaultValue;
}

export function createCalendarResources(propertyResources: PropertyResource[] | undefined, advBookHours: number = 0): CalendarResource[] {
  const list: CalendarResource[] = [];
  const startDate = getMinStartDate(propertyResources);
  const endDate = getMaxEndDate(propertyResources);
  if (!propertyResources || !startDate || !endDate || propertyResources.length === 0) {
    return list;
  }

  const startDayHour = getMinStartHourOfDay(propertyResources);
  const endDayHour = getMaxEndHourOfDay(propertyResources);

  debug(`startDate=${startDate?.format(CELL_ID_FORMAT)}, endDate=${endDate?.format(CELL_ID_FORMAT)}`);
  debug(`startDayHour=${startDayHour}, endDayHour=${endDayHour}`);
  if (!propertyResources || !startDate || !endDate || !startDayHour || !endDayHour) {
    return list;
  }
  console.log('Adv In Util '+advBookHours);
  const minStartTime = moment().add(advBookHours, "hours"); // two hour advanced booking time
  propertyResources.forEach((pr) => {
    if (pr.timeSlots) {
      pr.timeSlots.forEach((timeslot) => {
        if (minStartTime.isSameOrBefore(timeslot.startTime)) {
          const avail = {
            id: `r${list.length}`,
            name: pr.name,
            propertyId: pr.propertyId,
            startTime: moment(timeslot.startTime),
            endTime: moment(timeslot.endTime),
            remainingTours: timeslot.remainingTours,
            tourType: timeslot.tourType
          };
          list.push(avail);
        }
      });
    }
  });
  return list;
}

export function createColumns(targetDate: Moment, startDayOfWeek = 0, numberOfDays = 7): Array<CalendarColumn> {
  const dayColumns: Array<CalendarColumn> = [];
  const workingDate: Moment = moment(targetDate);
  workingDate.day(startDayOfWeek);
  workingDate.hour(0);
  workingDate.minute(0);
  workingDate.second(0);
  workingDate.millisecond(0);
  // Empty Column for time slot
  dayColumns.push({
    id: TIME_COLUMN_ID,
    columnDate: moment(workingDate),
  });
  for (let i = 0; i < numberOfDays; i++) {
    dayColumns.push({
      id: moment(workingDate).format(COLUMN_ID_FORMAT),
      columnDate: moment(workingDate),
    });
    workingDate.add(1, "days"); // ready for next
  }
  return dayColumns;
}

export function getTimeColumn(calendarColumns: CalendarColumn[]): CalendarColumn | undefined {
  let foundColumn = undefined;
  calendarColumns.forEach((column) => {
    if (column.id === TIME_COLUMN_ID) {
      foundColumn = column;
    }
  });
  return foundColumn;
}

// export function isRowTimeCellsBetweenDates(row: CalendarRow, start: Moment, end: Moment): boolean {
//   let isBetween = false;
//   if (row && row.timeCells && row.timeCells.length > 0) {
//     if (row.timeCells[0])
//   }
//   return isBetween;
// }
// export function getRowsByDateRange(rowArray: CalendarRow[], start: Moment, end: Moment) : CalendarRow[] {
//   const newArray = rowArray.filter( r => isRowTimeCellsBetweenDates(r, start, end) );
//   debug("getRowsByDateRange: ", newArray);
//   return newArray;
// }

export function getRowByHour(rowArray: CalendarRow[], hourOfDay: number, minute: number): CalendarRow | any {
  const rowTime = moment(); // Date is not important, use time of day
  rowTime.hour(hourOfDay);
  rowTime.minute(minute);
  rowTime.second(0);
  rowTime.millisecond(0);
  const rowId = rowTime.format(ROW_ID_FORMAT);

  let row = null;
  rowArray.forEach((r) => {
    if (r.id === rowId) {
      row = r;
    }
  });
  return row;
}


export function createRows(
  resources?: CalendarResource[],
  calendarColumns?: CalendarColumn[],
  startHour?: number,
  endHour?: number
): Array<CalendarRow> {
  const rowArray: Array<CalendarRow> = [];
  if (!resources || !calendarColumns || !startHour || !endHour) {
    return rowArray;
  }

  // Create one Row for each time slot
  for (let i = startHour; i <= endHour; i++) {
    let rowTime = moment(); // Date is not important, use time of day
    rowTime.hour(i);
    rowTime.minute(0);
    rowTime.second(0);
    rowTime.millisecond(0);
    let rowId = rowTime.format(ROW_ID_FORMAT);
    let row = {
      id: rowId,
      startTime: moment(rowTime), // time for legend
      timeCells: [], // list of timeCells for each day of week
    };
    rowArray.push(row);

    rowTime = moment(); // Date is not important, use time of day
    rowTime.hour(i);
    rowTime.minute(30);
    rowTime.second(0);
    rowTime.millisecond(0);
    rowId = rowTime.format(ROW_ID_FORMAT);
    row = {
      id: rowId,
      startTime: moment(rowTime), // time for legend
      timeCells: [], // list of timeCells for each day of week
    };
    rowArray.push(row);

  }

  // Add timeCells to each row based on day of week.
  calendarColumns.forEach((column) => {
    for (let i = startHour; i <= endHour; i++) {
      let row = getRowByHour(rowArray, i, 0);
      if (row) {
        const dateTime = moment(column.columnDate);
        dateTime.hour(i);
        dateTime.minute(0);
        dateTime.second(0);
        dateTime.millisecond(0);
        const endTime = moment(dateTime);
        endTime.add(1, "hours");
        const timeResources = column.id !== TIME_COLUMN_ID ? resourcesByDateTime(resources, dateTime) : [];
        const cellId = column.id !== TIME_COLUMN_ID ? dateTime.format(CELL_ID_FORMAT) : TIME_COLUMN_ID + "-" + i;
        row.timeCells.push({
          id: cellId,
          startTime: moment(dateTime),
          endTime: moment(endTime),
          selectedResource: "-1",
          resources: timeResources,
          cellType: column.id !== TIME_COLUMN_ID ? RESOURCE_TYPE : LEDGEND_TYPE,
        });
      }
      row = getRowByHour(rowArray, i, 30);
      if (row) {
        const dateTime = moment(column.columnDate);
        dateTime.hour(i);
        dateTime.minute(30);
        dateTime.second(0);
        dateTime.millisecond(0);
        const endTime = moment(dateTime);
        endTime.add(1, "hours");
        const timeResources = column.id !== TIME_COLUMN_ID ? resourcesByDateTime(resources, dateTime) : [];
        const cellId = column.id !== TIME_COLUMN_ID ? dateTime.format(CELL_ID_FORMAT) : TIME_COLUMN_ID + "-" + i;
        row.timeCells.push({
          id: cellId,
          startTime: moment(dateTime),
          endTime: moment(endTime),
          selectedResource: "-1",
          resources: timeResources,
          cellType: column.id !== TIME_COLUMN_ID ? RESOURCE_TYPE : LEDGEND_TYPE,
        });
      }
    }
  });
  return rowArray;
}

export function resourcesByDateTime(resources: CalendarResource[], dateTime: Moment): CalendarResource[] {
  const list = resources.filter((r) => dateTime.isSameOrAfter(r.startTime) && dateTime.isBefore(r.endTime));
  return list;
}

export function resourcesByPropertyId(resources: CalendarResource[], propertyId: string): CalendarResource[] {
  return resources.filter((r) => r.propertyId === propertyId);
}

export function getTimeCellById(rows: CalendarRow[], id: string): TimeCell | undefined {
  let timeCell = undefined;
  rows.forEach((row) => {
    row.timeCells.forEach((t) => {
      if (t.id === id) {
        timeCell = t;
      }
    });
  });
  return timeCell;
}

export function getTimeCellResourceById(timeCell: TimeCell, id: string, tourType: string): CalendarResource | undefined {
  let foundResource = undefined;
  if (timeCell.resources) {
    timeCell.resources.forEach((r) => {
      if (r.id === id && r.tourType === tourType) {
        foundResource = r;
      }
    });
  }
  return foundResource;
}

export function getTimeCellsByResourceId(calendarRows: CalendarRow[], resourceId: string, tourType: string): TimeCell[] {
  const timeCells = new Array<TimeCell>();
  if (calendarRows) {
    calendarRows.forEach((row) => {
      if (row.timeCells) {
        row.timeCells.forEach((cell) => {
          if (getTimeCellResourceById(cell, resourceId, tourType)) {
            // this cell contains the resource ID
            timeCells.push(cell);
          }
        });
      }
    });
  }
  return timeCells;
}

export function getTimeCellsByPropertyId(
  allResources?: CalendarResource[],
  allRows?: CalendarRow[],
  referenceTimeCell?: TimeCell,
  excludeReferenceTimeCell = true,
  tourType?: string,
): TimeCell[] {
  let communityCells = new Array<TimeCell>();

  if (!allResources || !allRows || !referenceTimeCell) {
    return communityCells;
  }

  let resourceList = resourcesByPropertyId(allResources, referenceTimeCell.selectedResource?.propertyId || "");
  if (resourceList) {
    if (resourceList && excludeReferenceTimeCell) {
      // remove reference Time Cell resource
      resourceList = resourceList.filter((r) => r.id !== referenceTimeCell.selectedId);
    }
    resourceList.forEach((r) => {
      const resourceTimeCells: TimeCell[] = getTimeCellsByResourceId(allRows, r.id,tourType);
      communityCells = communityCells.concat(resourceTimeCells);
    });
  }
  return communityCells;
}

export function getSelectedResources(rows: CalendarRow[]): CalendarResource[] {
  const selectedResouces: CalendarResource[] = [];
  rows.forEach((row) => {
    row.timeCells.forEach((cell) => {
      if (cell.selectedId && cell.selectedId !== NONE_ID && cell.selectedResource) {
        selectedResouces.push(cell.selectedResource);
      }
    });
  });
  return selectedResouces.sort((r1, r2) => {
    return r1.startTime.valueOf() - r2.startTime.valueOf();
  });
}
